import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, ClipboardList, Clock, CheckCircle2, Lock } from "lucide-react";

/**
 * Simple employee landing page.
 *
 * Shows the test person's own recent tests + 2 big actions: "Log a new test"
 * and "View all my tests". Intentionally distraction-free — perfect for shop
 * floor staff who just need to enter test results, not navigate complex menus.
 */
export default function EmployeeHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);
  const [counts, setCounts] = useState({ Pending: 0, Tested: 0, Complete: 0 });

  useEffect(() => {
    api
      .get("/tests", { params: { mine_only: true } })
      .then((res) => {
        const data = res.data || [];
        setRecent(data.slice(0, 5));
        const c = { Pending: 0, Tested: 0, Complete: 0 };
        data.forEach((t) => {
          c[t.status] = (c[t.status] || 0) + 1;
        });
        setCounts(c);
      })
      .catch(() => setRecent([]));
  }, []);

  return (
    <div className="p-4 md:p-10 max-w-6xl mx-auto">
      {/* Greeting */}
      <div className="mb-8">
        <div className="subtitle text-[11px] uppercase tracking-[0.3em] text-[#39678f] mb-2">
          Welcome
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#33424f] leading-none break-words">
          {user?.name?.toUpperCase()}
        </h1>
        <p className="font-body text-base text-[#50493b] mt-3">
          Choose what you'd like to do. New tests are credited to you automatically.
        </p>
      </div>

      {/* Big actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        <Button
          data-testid="emp-action-new-test"
          onClick={() => navigate("/tests/new")}
          className="bg-[#39678f] hover:bg-[#2c5272] text-white h-32 flex-col gap-2 text-base sm:text-xl subtitle uppercase tracking-[0.15em] border-0 whitespace-normal text-center leading-tight px-3"
        >
          <Plus className="w-10 h-10" />
          Log a new pressure test
        </Button>
        <Button
          data-testid="emp-action-my-tests"
          onClick={() => navigate("/tests")}
          variant="outline"
          className="bg-white border border-[#33424f]/30 text-[#33424f] hover:bg-[#e6e0db] h-32 flex-col gap-2 text-base sm:text-xl subtitle uppercase tracking-[0.15em] whitespace-normal text-center leading-tight px-3"
        >
          <ClipboardList className="w-10 h-10" />
          View all tests
        </Button>
      </div>

      {/* My counts */}
      <div className="grid grid-cols-3 gap-0 border border-[#33424f]/20 bg-white mb-10 hide-in-hc">
        {[
          { key: "Pending", icon: Clock, accent: "text-[#ccb795]" },
          { key: "Tested", icon: CheckCircle2, accent: "text-[#39678f]" },
          { key: "Complete", icon: Lock, accent: "text-[#33424f]" },
        ].map(({ key, icon: Icon, accent }, i) => (
          <div
            key={key}
            data-testid={`emp-stat-${key.toLowerCase()}`}
            className={`p-5 ${i < 2 ? "border-r border-[#33424f]/20" : ""}`}
          >
            <Icon className={`w-5 h-5 ${accent} mb-3`} />
            <div className="font-display text-5xl text-[#33424f] leading-none">{counts[key] || 0}</div>
            <div className="subtitle text-[10px] uppercase tracking-[0.2em] text-[#50493b] mt-2">
              My {key} tests
            </div>
          </div>
        ))}
      </div>

      {/* Recent tests */}
      <div className="subtitle text-[11px] uppercase tracking-[0.2em] text-[#39678f] mb-3">
        My most recent tests
      </div>
      <div className="bg-white border border-[#33424f]/20">
        {recent.length === 0 ? (
          <div className="p-10 text-center text-[#50493b]">
            You haven't logged any tests yet. Tap "Log a new pressure test" above to get started.
          </div>
        ) : (
          <>
            <table className="hidden md:table w-full data-table text-base">
              <thead>
                <tr>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Job</th>
                  <th className="text-left px-4 py-3">Vessel</th>
                  <th className="text-left px-4 py-3">Result</th>
                  <th className="text-left px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((t) => (
                  <tr
                    key={t.id}
                    data-testid={`emp-recent-${t.id}`}
                    onClick={() => navigate(`/tests/${t.id}/certificate`)}
                    className="border-t border-[#33424f]/10 hover:bg-[#f5f1ec] cursor-pointer"
                  >
                    <td className="px-4 py-3 subtitle uppercase text-xs">{t.status}</td>
                    <td className="px-4 py-3 font-semibold">{t.job_number}</td>
                    <td className="px-4 py-3">{t.vessel_type}{t.vessel_number ? ` #${t.vessel_number}` : ""}</td>
                    <td className="px-4 py-3">{t.test_result || "Pending"}</td>
                    <td className="px-4 py-3 text-[#50493b]">{t.test_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-[#33424f]/10" data-testid="emp-recent-cards">
              {recent.map((t) => (
                <div
                  key={t.id}
                  data-testid={`emp-recent-card-${t.id}`}
                  onClick={() => navigate(`/tests/${t.id}/certificate`)}
                  className="p-4 cursor-pointer active:bg-[#f5f1ec] select-none"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-0.5">Job</div>
                      <div className="font-semibold text-[#33424f] truncate">{t.job_number}</div>
                    </div>
                    <span className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#33424f] border border-[#33424f]/20 bg-[#f5f1ec] px-2 py-1 shrink-0">
                      {t.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm font-body">
                    <div>
                      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-0.5">Vessel</div>
                      <div className="text-[#33424f] truncate">{t.vessel_type}{t.vessel_number ? ` #${t.vessel_number}` : ""}</div>
                    </div>
                    <div>
                      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-0.5">Result</div>
                      <div className="text-[#33424f] truncate">{t.test_result || "Pending"}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[10px] subtitle uppercase tracking-[0.2em] text-[#50493b] mb-0.5">Date</div>
                      <div className="text-[#50493b]">{t.test_date || "—"}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
